import { Spin } from 'antd'
import { useQuantitativeDataSummaryStats } from 'features/dataLayers'
import { IndicationItem } from 'features/indicationTriage/api/indicationTriageApi'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_CORROSION_LOCATION,
  LABEL_D_INNER,
  LABEL_D_ML,
  LABEL_D_OUTER,
  LABEL_NOMINAL,
  LABEL_T_ML,
  LABEL_TC,
  LABEL_TMM,
  LABEL_TRD,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { FfsParameterSwitch } from 'features/indicationTriage/cards/ffsCalc/FfsParameterSwitch'
import { WORKING_UNIT_THICKNESS } from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import {
  GET_DIAMTERS,
  getFlawLengths,
  getLongitudinalAxis,
} from 'features/indicationTriage/cards/ffsCalc/part5LevelOne/FfsDataValuesSection'
import { useWindowFrameCdf } from 'features/indicationTriage/cards/ffsCalc/useWindowFrame'
import { useCroppedCdfSummaryStats } from 'features/indicationTriage/cards/indicationOverview/useCroppedCdfSummaryStats'
import isNil from 'lodash/isNil'
import React, { Dispatch, FC, SetStateAction, useMemo } from 'react'
import {
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoMeasurement,
} from 'types'

interface FfsPartFourDataValueSectionProps {
  loading: boolean
  tmm: GeckoMeasurement | null
  trd: GeckoMeasurement | null
  tc: GeckoMeasurement | null
  t_ml: GeckoMeasurement | null
  d_ml: GeckoMeasurement | null
  longitudinalFlawLength: GeckoMeasurement | null
  latitudinalFlawLength: GeckoMeasurement | null
  innerDiameter: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  nominal: GeckoMeasurement | null
  corrosionOnInnerSurface: boolean
  setCorrosionOnInnerSurface: Dispatch<SetStateAction<boolean>>
}

type HookParams = {
  indicationItem: IndicationItem | undefined
  componentModel: GeckoComponentModel | undefined
  thicknessCdf: GeckoComponentDataFrame | undefined
  fca: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  corrosionOnInnerSurface: boolean
  setCorrosionOnInnerSurface: Dispatch<SetStateAction<boolean>>
}

export function useFfsPartFourDataValues({
  indicationItem,
  fca,
  fcaMl,
  componentModel,
  thicknessCdf,
  corrosionOnInnerSurface,
  setCorrosionOnInnerSurface,
}: HookParams): FfsPartFourDataValueSectionProps {
  // TODO(ffs-preplanning): this crop to `indicationItem.selection` is what makes
  // Part 4 PER-LTA. API 579 Part 4 (general metal loss) is really a
  // component/region-level assessment; a component-level Part 4 would use the
  // full thickness CDF (or an engineer-chosen region) here instead of a single
  // LTA box. Revisit with the FFS pre-planning step. See FfsPartFourStep.tsx.
  const { stats: cdfStatsThickness, isLoading: cdfStatsThicknessLoading } =
    useCroppedCdfSummaryStats(thicknessCdf, indicationItem?.selection)
  const tmm = cdfStatsThickness?.value_range.min ?? null

  // We take a 'window frame' around the indication to get a trd
  const { cdf: windowFrameCdf, isLoading: windowFrameLoading } =
    useWindowFrameCdf(thicknessCdf, indicationItem?.selection, 3)
  const { data: windowFrameStats, isLoading: windowFrameStatsLoading } =
    useQuantitativeDataSummaryStats(windowFrameCdf ?? undefined)

  const loading =
    windowFrameLoading || cdfStatsThicknessLoading || windowFrameStatsLoading
  const trd = windowFrameStats?.value_range.min ?? null

  const longitundinalAxis: 'x' | 'y' = useMemo(
    () => getLongitudinalAxis(componentModel),
    [componentModel]
  )
  const [longitudinalFlawLength, latitudinalFlawLength] = useMemo(
    () =>
      indicationItem?.selection
        ? getFlawLengths(longitundinalAxis, indicationItem?.selection)
        : [null, null],
    [indicationItem, longitundinalAxis]
  )

  const [innerDiameter, outerDiameter, nominal]: [
    GeckoMeasurement | null,
    GeckoMeasurement | null,
    GeckoMeasurement | null,
  ] = useMemo(
    () =>
      componentModel
        ? (GET_DIAMTERS[componentModel.type]?.(
            componentModel,
            tmm,
            corrosionOnInnerSurface
          ) ?? [null, null, null])
        : [null, null, null],
    [componentModel, corrosionOnInnerSurface, tmm]
  )

  const tc = useMemo(
    () =>
      isNil(trd) || isNil(fca)
        ? null
        : GeckoMeasurement.CreateFromValue(
            trd.convertTo(WORKING_UNIT_THICKNESS).num -
              fca.convertTo(WORKING_UNIT_THICKNESS).num,
            WORKING_UNIT_THICKNESS
          ),
    [fca, trd]
  )

  const t_ml = useMemo(
    () =>
      isNil(nominal) || isNil(fcaMl)
        ? null
        : GeckoMeasurement.CreateFromValue(
            nominal.convertTo(WORKING_UNIT_THICKNESS).num -
              fcaMl.convertTo(WORKING_UNIT_THICKNESS).num,
            WORKING_UNIT_THICKNESS
          ),
    [nominal, fcaMl]
  )

  const d_ml = useMemo(
    () =>
      isNil(innerDiameter) || isNil(fcaMl)
        ? null
        : GeckoMeasurement.CreateFromValue(
            innerDiameter.convertTo(WORKING_UNIT_THICKNESS).num +
              2 * fcaMl.convertTo(WORKING_UNIT_THICKNESS).num,
            WORKING_UNIT_THICKNESS
          ),
    [innerDiameter, fcaMl]
  )

  return useMemo(
    () => ({
      loading,
      tmm,
      trd,
      tc,
      nominal,
      t_ml,
      longitudinalFlawLength,
      latitudinalFlawLength,
      innerDiameter,
      outerDiameter,
      d_ml,
      corrosionOnInnerSurface,
      setCorrosionOnInnerSurface,
    }),
    [
      loading,
      tmm,
      trd,
      tc,
      nominal,
      t_ml,
      longitudinalFlawLength,
      latitudinalFlawLength,
      innerDiameter,
      outerDiameter,
      d_ml,
      corrosionOnInnerSurface,
      setCorrosionOnInnerSurface,
    ]
  )
}

const RawFfsDataValueSection: FC<FfsPartFourDataValueSectionProps> = ({
  loading,
  tmm,
  trd,
  tc,
  nominal,
  t_ml,
  longitudinalFlawLength,
  latitudinalFlawLength,
  innerDiameter,
  outerDiameter,
  d_ml,
  corrosionOnInnerSurface,
  setCorrosionOnInnerSurface,
}) => {
  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterSwitch
          label={LABEL_CORROSION_LOCATION}
          labelWidth={LABEL_WIDTH}
          value={corrosionOnInnerSurface}
          setValue={setCorrosionOnInnerSurface}
          checkedContent='Inner Surface'
          uncheckedContent='Outer Surface'
          labelTooltip='Adjust inner diameter for corrosion'
        />
        <FfsParameterField
          label={LABEL_TMM}
          labelTooltip='Minimum Measured within corrosion area'
          labelWidth={LABEL_WIDTH}
          value={tmm}
        />
        <FfsParameterField
          label={LABEL_NOMINAL}
          labelTooltip='Nominal thickness'
          labelWidth={LABEL_WIDTH}
          value={nominal}
        />
        <FfsParameterField
          label={LABEL_TRD}
          labelTooltip='Minimum measured just outside of corrosion area'
          labelWidth={LABEL_WIDTH}
          value={trd}
        />
        <FfsParameterField
          label={LABEL_TC}
          labelTooltip={
            <>
              Future corroded wall thickness just outside of corrosion area
              <TooltipDivider />
              {LABEL_TRD} - FCA
            </>
          }
          labelWidth={LABEL_WIDTH}
          value={tc}
        />
        <FfsParameterField
          label={LABEL_T_ML}
          labelTooltip={
            <>
              Nominal thickness in the region of corrosion corrected for FCA, as
              applicable
              <TooltipDivider />t<Sub>nom</Sub> - FCA<Sub>ml</Sub>
            </>
          }
          labelWidth={LABEL_WIDTH}
          value={t_ml}
        />
        <FfsParameterField
          label='s'
          labelTooltip='Longitudinal length of metal loss region'
          labelWidth={LABEL_WIDTH}
          value={longitudinalFlawLength}
        />
        <FfsParameterField
          label='c'
          labelTooltip='Circumferential length of metal loss region'
          labelWidth={LABEL_WIDTH}
          value={latitudinalFlawLength}
        />
        <FfsParameterField
          label={LABEL_D_OUTER}
          labelTooltip='Outer diameter of the vessel'
          labelWidth={LABEL_WIDTH}
          value={outerDiameter}
          measurementType='length'
        />
        <FfsParameterField
          label={LABEL_D_INNER}
          labelTooltip='Inside diameter of the vessel'
          labelWidth={LABEL_WIDTH}
          value={innerDiameter}
        />
        <FfsParameterField
          label={LABEL_D_ML}
          labelTooltip={
            <>
              Inside diameter of the shell corrected for FCA, as applicable
              <TooltipDivider />D<Sub>inner</Sub> + 2 × FCA<Sub>ml</Sub>
            </>
          }
          labelWidth={LABEL_WIDTH}
          value={d_ml}
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsPartFourDataValueSection = React.memo(RawFfsDataValueSection)
